/**
 * Plain-text diagnostic report, meant to be PASTED as-is.
 *
 * Born from a concrete case: the app got stuck "syncing" forever and the
 * reason was that the PowerSync service was not even up. From the
 * screen there was no way to tell "not connecting" from "connecting and going slow", and
 * describing it by hand loses half the data.
 *
 * SINGLE SOURCE. The first version received its own object while the screen
 * painted its rows on its own: two sources for the same thing, so adding a
 * row to the UI left the report incomplete without anyone noticing. Now
 * both consume the SAME array of sections: the screen renders it, this
 * function serializes it and the hint is computed by reading it.
 *
 * Each item carries three fields for different reasons:
 *   · `label`/`value` — already-translated text: what is seen and what is copied.
 *   · `status`        — only the UI uses it for the color; the report ignores it.
 *   · `raw`           — UNtranslated value, so the logic does not depend on the
 *                       language. Without it, the hint would stop firing as soon as
 *                       someone changed a translation.
 *
 * It does NOT include personal data: no email, no name, no token. Whoever builds
 * the sections is responsible for trimming identifiers.
 */

export type DiagnosticStatus = "success" | "warning" | "danger" | "neutral";

export interface DiagnosticItem {
  /** Stable identifier, independent of language. Used by the logic. */
  key: string;
  /** Already-translated label. */
  label: string;
  /** Already-translated / formatted value, as seen on screen. */
  value: string;
  /** Only for the UI. */
  status?: DiagnosticStatus;
  /** Raw value for logic that must not depend on language. */
  raw?: string | number | boolean | null;
}

export interface DiagnosticSection {
  title: string;
  items: DiagnosticItem[];
}

export interface DiagnosticsReportInput {
  appName: string;
  /** ISO timestamp. It is passed in; the clock is not read here (pure function). */
  timestamp: string;
  sections: DiagnosticSection[];
}

/** Looks up by `key`, never by label: labels are translated. */
function findItem(sections: DiagnosticSection[], key: string): DiagnosticItem | undefined {
  for (const s of sections) {
    const found = s.items.find((i) => i.key === key);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/**
 * Actionable hint for the pattern that started all this: there is a session and
 * sync does not start. It is almost always the service being down, not the app, and
 * saying so here saves looking in the wrong place.
 */
function diagnosticHint(sections: DiagnosticSection[]): string[] {
  const session = findItem(sections, "session");
  const sync = findItem(sections, "sync");
  if (
    session?.raw !== true ||
    !sync ||
    sync.raw === "connected" ||
    sync.raw === undefined ||
    sync.raw === null
  ) {
    return [];
  }
  return [
    "",
    "## Pista",
    `La sesión está activa pero PowerSync no llega a "connected" (${String(sync.raw)}).`,
    "Comprobar que el servicio de PowerSync responde en la URL de arriba: si da",
    "502 o no responde, el problema es la pila, no la app.",
  ];
}

export function buildDiagnosticsReport(input: DiagnosticsReportInput): string {
  const parts: string[] = [`# Diagnóstico — ${input.appName}`, input.timestamp];

  for (const section of input.sections) {
    parts.push("", `## ${section.title}`);
    for (const item of section.items) {
      // An empty field is FLAGGED instead of omitted: in a pasted report, a
      // missing datum and an uncollected one read the same if it is not said which it is.
      parts.push(`- ${item.label}: ${item.value === "" ? "(sin dato)" : item.value}`);
    }
  }

  parts.push(...diagnosticHint(input.sections));
  return parts.join("\n");
}
